import React, { useState } from 'react'
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts'
import { SYNTHETIC_TEAM_WORKLOAD, WeeklyWorkloadTeam } from './syntheticDashboardData'
import { Activity, Clock } from 'lucide-react'

interface WorkloadStackedChartProps {
  data?: WeeklyWorkloadTeam[]
  compact?: boolean
  showAverages?: boolean
  onNavigateToStats?: () => void
}

export const WorkloadStackedChart: React.FC<WorkloadStackedChartProps> = (props) => {
  const {
    data = SYNTHETIC_TEAM_WORKLOAD,
    compact = false,
    showAverages = true,
  } = props
  const [metricMode, setMetricMode] = useState<'minutes' | 'srpe'>('minutes')
  const [showRpeLine, setShowRpeLine] = useState(true)

  // Color tokens according to DESIGN_SYSTEM.md
  const colors = {
    flag: '#101e33',       // Azul Noche institucional
    strength: '#b91c1c',   // Rojo deportivo FEFA
    club: '#d97706',       // Ámbar acento
    match: '#4338ca',      // Índigo competición
    avgLine: '#dc2626',    // Línea de RPE medio
  }

  // Pre-calculate chart dataset according to metricMode
  const chartData = data.map((d) => {
    if (metricMode === 'minutes') {
      return {
        name: d.week,
        startDate: d.startDate,
        Flag: d.flagMinutes,
        Fuerza: d.strengthMinutes,
        Club: d.clubMinutes,
        Partido: d.matchMinutes,
        total: d.totalMinutes,
        avgRPE: d.avgSRPE,
        sample: d.sampleSize,
      }
    } else {
      // Approximate sRPE distribution across disciplines
      const factor = d.totalSRPE / (d.totalMinutes || 1)
      return {
        name: d.week,
        startDate: d.startDate,
        Flag: Math.round(d.flagMinutes * factor),
        Fuerza: Math.round(d.strengthMinutes * factor),
        Club: Math.round(d.clubMinutes * factor),
        Partido: Math.round(d.matchMinutes * factor),
        total: d.totalSRPE,
        avgRPE: d.avgSRPE,
        sample: d.sampleSize,
      }
    }
  })

  const currentWeek = data[data.length - 1]
  const previousWeek = data[data.length - 2]
  const deltaMinutes = currentWeek.totalMinutes - previousWeek.totalMinutes
  const deltaSRPE = currentWeek.totalSRPE - previousWeek.totalSRPE

  return (
    <div className="workload-card" style={{
      background: 'var(--surface-card, #ffffff)',
      border: '1px solid var(--surface-border, #e2e8f0)',
      borderRadius: '8px',
      padding: compact ? '1rem' : '1.25rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      {/* Header with Title and Mode Switcher */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '0.75rem',
        marginBottom: '1rem',
      }}>
        <div>
          <div style={{
            fontSize: compact ? '0.75rem' : '0.8rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--text-muted, #64748b)',
          }}>
            Evolución Semanal de Carga por Disciplina
          </div>
          <div style={{
            fontSize: compact ? '1.1rem' : '1.35rem',
            fontWeight: 800,
            color: 'var(--text-main, #0f172a)',
            fontVariantNumeric: 'tabular-nums',
            marginTop: '2px',
          }}>
            {metricMode === 'minutes' ? `${currentWeek.totalMinutes} min` : `${currentWeek.totalSRPE} UA sRPE`}
            {' '}
            <span style={{
              fontSize: '0.82rem',
              fontWeight: 600,
              color: metricMode === 'minutes'
                ? deltaMinutes >= 0 ? '#15803d' : '#b45309'
                : deltaSRPE >= 0 ? '#15803d' : '#b45309',
            }}>
              ({metricMode === 'minutes' ? `${deltaMinutes >= 0 ? '+' : ''}${deltaMinutes} min vs S7` : `${deltaSRPE >= 0 ? '+' : ''}${deltaSRPE} UA vs S7`})
            </span>
            {' '}
            <span style={{ fontSize: '0.8rem', fontWeight: 500, color: 'var(--text-muted, #64748b)' }}>
              · Muestra activa: n = {currentWeek.sampleSize} atletas
            </span>
          </div>
        </div>

        {/* Metric toggles */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <div style={{
            display: 'inline-flex',
            background: 'var(--surface-subtle, #f8fafc)',
            border: '1px solid var(--surface-border, #e2e8f0)',
            borderRadius: '6px',
            padding: '2px',
          }}>
            <button
              type="button"
              onClick={() => setMetricMode('minutes')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '4px',
                border: 'none',
                background: metricMode === 'minutes' ? '#ffffff' : 'transparent',
                color: metricMode === 'minutes' ? 'var(--text-main, #0f172a)' : 'var(--text-secondary, #475569)',
                fontWeight: metricMode === 'minutes' ? 700 : 500,
                fontSize: '0.75rem',
                cursor: 'pointer',
                boxShadow: metricMode === 'minutes' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <Clock size={13} />
              Minutos
            </button>
            <button
              type="button"
              onClick={() => setMetricMode('srpe')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '4px 10px',
                borderRadius: '4px',
                border: 'none',
                background: metricMode === 'srpe' ? '#ffffff' : 'transparent',
                color: metricMode === 'srpe' ? 'var(--text-main, #0f172a)' : 'var(--text-secondary, #475569)',
                fontWeight: metricMode === 'srpe' ? 700 : 500,
                fontSize: '0.75rem',
                cursor: 'pointer',
                boxShadow: metricMode === 'srpe' ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              <Activity size={13} />
              Carga (sRPE)
            </button>
          </div>

          {showAverages && (
            <button
              type="button"
              onClick={() => setShowRpeLine(!showRpeLine)}
              style={{
                padding: '5px 8px',
                borderRadius: '6px',
                border: `1px solid ${showRpeLine ? '#fecaca' : 'var(--surface-border, #e2e8f0)'}`,
                background: showRpeLine ? '#fee2e2' : '#ffffff',
                color: showRpeLine ? '#b91c1c' : 'var(--text-secondary, #475569)',
                fontSize: '0.75rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Línea RPE
            </button>
          )}
        </div>
      </div>

      {/* Chart Container */}
      <div style={{
        width: '100%',
        height: compact ? 220 : 280,
        fontFamily: "'Inter', sans-serif",
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
            <XAxis
              dataKey="name"
              tickLine={false}
              axisLine={{ stroke: '#cbd5e1' }}
              tick={{ fill: '#64748b', fontSize: 11, fontWeight: 600 }}
            />
            <YAxis
              yAxisId="left"
              tickLine={false}
              axisLine={{ stroke: '#cbd5e1' }}
              tick={{ fill: '#64748b', fontSize: 11 }}
              unit={metricMode === 'minutes' ? 'm' : ''}
            />
            {showRpeLine && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[4, 10]}
                tickLine={false}
                axisLine={false}
                tick={{ fill: '#b91c1c', fontSize: 10 }}
                unit=" rpe"
              />
            )}
            <Tooltip
              content={({ active, payload, label }) => {
                if (active && payload && payload.length) {
                  const item = payload[0]?.payload
                  return (
                    <div style={{
                      background: 'rgba(10, 19, 32, 0.95)',
                      color: '#f8fafc',
                      padding: '10px 12px',
                      borderRadius: '6px',
                      fontSize: '0.78rem',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      lineHeight: 1.4,
                    }}>
                      <div style={{ fontWeight: 700, marginBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: '3px' }}>
                        {label} ({item?.startDate})
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <span style={{ color: '#94a3b8' }}>Flag Football:</span>
                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.Flag} {metricMode === 'minutes' ? 'min' : 'UA'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <span style={{ color: '#94a3b8' }}>Fuerza / Cond.:</span>
                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.Fuerza} {metricMode === 'minutes' ? 'min' : 'UA'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <span style={{ color: '#94a3b8' }}>Club / Tackle:</span>
                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.Club} {metricMode === 'minutes' ? 'min' : 'UA'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <span style={{ color: '#94a3b8' }}>Partido:</span>
                        <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.Partido} {metricMode === 'minutes' ? 'min' : 'UA'}</strong>
                      </div>
                      <div style={{ marginTop: '4px', paddingTop: '4px', borderTop: '1px solid rgba(255,255,255,0.15)', display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                        <span>Total Carga:</span>
                        <strong style={{ color: '#fef08a', fontVariantNumeric: 'tabular-nums' }}>{item?.total} {metricMode === 'minutes' ? 'min' : 'UA'}</strong>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', color: '#93c5fd' }}>
                        <span>RPE medio / Atletas:</span>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{item?.avgRPE} / 10 (n={item?.sample})</span>
                      </div>
                    </div>
                  )
                }
                return null
              }}
            />
            <Legend
              verticalAlign="bottom"
              height={26}
              iconSize={10}
              formatter={(value) => (
                <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#475569' }}>
                  {value}
                </span>
              )}
            />
            <Bar yAxisId="left" dataKey="Flag" stackId="a" fill={colors.flag} radius={[0, 0, 0, 0]} />
            <Bar yAxisId="left" dataKey="Fuerza" stackId="a" fill={colors.strength} radius={[0, 0, 0, 0]} />
            <Bar yAxisId="left" dataKey="Club" stackId="a" fill={colors.club} radius={[0, 0, 0, 0]} />
            <Bar yAxisId="left" dataKey="Partido" stackId="a" fill={colors.match} radius={[2, 2, 0, 0]} />
            {showRpeLine && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="avgRPE"
                name="RPE medio"
                stroke={colors.avgLine}
                strokeWidth={2}
                dot={{ r: 3, fill: colors.avgLine }}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
